import { useYearPeriodStore } from '../../store/useYearPeriodStore'
import { Spinner } from '@heroui/react'
import { StationPanelLinePhoto } from '../panels/StationPanelLinePhoto'
import { YearPeriodPicker } from './shared/YearPeriodPicker'
import { MlyGrid } from './shared/MlyGrid'
import { TableOrientationToggle } from './shared/TableOrientationToggle'
import { withShortLabels } from './shared/rowGroupsTransposed'
import { useTableOrientationStore } from '../../store/useTableOrientationStore'
import { useMonthlyData } from '../../hooks/useMonthlyData'
import { useMonthlyNormals } from '../../hooks/useMonthlyNormals'

const SLUG = 'mly-srazky'

const ROW_GROUPS = [
  {
    label: 'Srážky – hodnoty',
    rows: [
      { tsId: 'PRECIPSUM_SUM', label: 'Srážkový úhrn',        unit: 'mm', normalMode: 'pct',  decimals: 1 },
      { tsId: 'PRECIPSUM_MAX', label: 'Denní maximum srážek', unit: 'mm', normalMode: null,   decimals: 1 },
    ],
  },
  {
    label: 'Srážky – počty dní',
    rows: [
      { tsId: 'PRECIPSUM_G0',   label: 'Dny se srážkami ≥ 0.1 mm', unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'PRECIPSUM_GE1',  label: 'Dny se srážkami ≥ 1 mm',   unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'PRECIPSUM_GE5',  label: 'Dny se srážkami ≥ 5 mm',   unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'PRECIPSUM_GE10', label: 'Dny se srážkami ≥ 10 mm',  unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'PRECIPSUM_GE20', label: 'Dny se srážkami ≥ 20 mm',  unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'PRECIPSUM_GE30', label: 'Dny se srážkami ≥ 30 mm',  unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'PRECIPSUM_GE50', label: 'Dny se srážkami ≥ 50 mm',  unit: 'dny', normalMode: 'diff', decimals: 0 },
    ],
  },
  {
    label: 'Sněhová pokrývka – hodnoty',
    rows: [
      { tsId: 'SNOW_MAX',      label: 'Maximum sněhové pokrývky',     unit: 'cm', normalMode: null, decimals: 0 },
      { tsId: 'SNOWNEW_MAX',   label: 'Maximum nového sněhu',          unit: 'cm', normalMode: null, decimals: 0 },
      { tsId: 'SNOWWATER_MAX', label: 'Maximum vodní hodnoty sněhu',   unit: 'mm', normalMode: null, decimals: 0 },
    ],
  },
  {
    label: 'Sněhová pokrývka – počty dní',
    rows: [
      { tsId: 'SNOW_GE1',   label: 'Dny se sněhem ≥ 1 cm',    unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'SNOW_GE5',   label: 'Dny se sněhem ≥ 5 cm',    unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'SNOW_GE10',  label: 'Dny se sněhem ≥ 10 cm',   unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'SNOW_GE20',  label: 'Dny se sněhem ≥ 20 cm',   unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'SNOW_GE50',  label: 'Dny se sněhem ≥ 50 cm',   unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'SNOW_GE100', label: 'Dny se sněhem ≥ 100 cm',  unit: 'dny', normalMode: 'diff', decimals: 0 },
      { tsId: 'SNOWNEW_GE1',label: 'Dny s novým sněhem ≥ 1 cm',unit: 'dny', normalMode: 'diff', decimals: 0 },
    ],
  },
]

// 2. definice hlavičky – transponovaná tabulka. Počty dní → jen prahová hodnota.
const ROW_GROUPS_T = withShortLabels(ROW_GROUPS, {
  PRECIPSUM_SUM: 'Úhrn', PRECIPSUM_MAX: 'Denní max.',
  PRECIPSUM_G0: '≥ 0.1 mm', PRECIPSUM_GE1: '≥ 1 mm', PRECIPSUM_GE5: '≥ 5 mm',
  PRECIPSUM_GE10: '≥ 10 mm', PRECIPSUM_GE20: '≥ 20 mm', PRECIPSUM_GE30: '≥ 30 mm', PRECIPSUM_GE50: '≥ 50 mm',
  SNOW_MAX: 'Max. výška', SNOWNEW_MAX: 'Max. nový', SNOWWATER_MAX: 'Max. vod. hodnota',
  SNOW_GE1: '≥ 1 cm', SNOW_GE5: '≥ 5 cm', SNOW_GE10: '≥ 10 cm', SNOW_GE20: '≥ 20 cm',
  SNOW_GE50: '≥ 50 cm', SNOW_GE100: '≥ 100 cm', SNOWNEW_GE1: 'nový ≥ 1 cm',
}, {
  'Srážky – hodnoty': 'Srážky',
  'Srážky – počty dní': 'Dny se srážkami',
  'Sněhová pokrývka – hodnoty': 'Sníh',
  'Sněhová pokrývka – počty dní': 'Dny se sněhem',
})

const TS_IDS = ROW_GROUPS.flatMap(g => g.rows.map(r => r.tsId))

export function MlySrazkyView({ station }) {
  const { year, setYear } = useYearPeriodStore()
  const orientation = useTableOrientationStore((s) => s.getOrientation(SLUG))

  const { dataMap, yearMap, dtMap, yearDtMap, isLoading: dLoading, isError: dError } = useMonthlyData(station?.gh_id, year, TS_IDS)
  const { normalsMap, yearNormalsMap, isLoading: nLoading }        = useMonthlyNormals(station?.gh_id, TS_IDS)

  const isLoading = dLoading || nLoading

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-gray-200 bg-white flex flex-col gap-3">
        {station && <StationPanelLinePhoto station={station} />}
        <div className="flex items-center justify-between gap-3">
          <YearPeriodPicker year={year} onYearChange={setYear} obsType="DLY" />
          <TableOrientationToggle slug={SLUG} />
        </div>
      </div>
      <div className="flex-1 min-h-0 p-4 flex flex-col">
        {isLoading && <div className="flex items-center gap-2 text-sm text-gray-500"><Spinner size="sm" /> Načítání dat…</div>}
        {dError && <p className="text-red-500 text-sm">Chyba při načítání dat</p>}
        {!isLoading && !dError && (
          <MlyGrid
            rowGroups={ROW_GROUPS}
            rowGroupsTransposed={ROW_GROUPS_T}
            orientation={orientation}
            dataMap={dataMap}
            yearMap={yearMap}
            dtMap={dtMap}
            yearDtMap={yearDtMap}
            normalsMap={normalsMap}
            yearNormalsMap={yearNormalsMap}
          />
        )}
      </div>
    </div>
  )
}
